import type { SanityQuizBlock } from '@lib/sanity/types';

/** Block types that make up the readiness step flows (pre- and post-GW1). */
export const READINESS_STEP_BLOCK_TYPES = new Set([
  'readinessTeamImportBlock',
  'readinessTeamConfirmBlock',
  'readinessTemplateTeamBlock',
  'readinessLeagueNameBlock',
  'readinessCaptainBlock',
  'readinessTransferBlock',
  'readinessStakesBlock',
  'readinessEmailGateBlock',
  'readinessProcessingBlock',
  'readinessVerdictBlock',
  'readinessLeagueSelectBlock',
  'readinessRaceVerdictBlock'
]);

export const isReadinessStepFlow = (blocks: SanityQuizBlock[]): boolean =>
  blocks.some((block) => READINESS_STEP_BLOCK_TYPES.has(block._type));

// Blocks that are alternate branches of the same journey step share a
// group id and collapse into a single progress pip (confirm vs template
// pick are both "verify your team"; post-GW1 league select vs the
// league-type fallback are both "pick your target league"). Grouping
// only collapses CONSECUTIVE blocks, so the pre-GW1 stakes block — which
// sits between transfer and email, never beside a league-select block —
// keeps its own pip.
const PROGRESS_GROUP_BY_TYPE: Record<string, string> = {
  readinessTeamConfirmBlock: 'team-verify',
  readinessTemplateTeamBlock: 'team-verify',
  readinessLeagueSelectBlock: 'league-target',
  readinessStakesBlock: 'league-target'
};

export type ReadinessProgressStep = {
  groupId: string;
  blockIndexes: number[];
};

/**
 * Derives the progress pips programmatically from the page's blocks array:
 * one pip per step, with consecutive branch blocks collapsed into one.
 * Non-readiness blocks (e.g. a trailing paywall) carry no pip.
 */
export const deriveProgressSteps = (
  blocks: SanityQuizBlock[]
): ReadinessProgressStep[] => {
  const steps: ReadinessProgressStep[] = [];

  blocks.forEach((block, index) => {
    if (!READINESS_STEP_BLOCK_TYPES.has(block._type)) return;

    const groupId = PROGRESS_GROUP_BY_TYPE[block._type] ?? block._type;
    const previous = steps[steps.length - 1];

    if (previous && previous.groupId === groupId) {
      previous.blockIndexes.push(index);
    } else {
      steps.push({ groupId, blockIndexes: [index] });
    }
  });

  return steps;
};

/**
 * The pip index for the currently active block. Returns the matching pip
 * when the active block is a step; `steps.length` (every pip complete) once
 * the journey is past the final step — e.g. the trailing paywall — so the
 * bar reads as finished rather than reset; otherwise -1.
 */
export const getCurrentProgressStep = (
  steps: ReadinessProgressStep[],
  currentBlockIndex: number
): number => {
  const matched = steps.findIndex((step) =>
    step.blockIndexes.includes(currentBlockIndex)
  );
  if (matched !== -1) return matched;

  const lastStep = steps[steps.length - 1];
  if (lastStep && currentBlockIndex > Math.max(...lastStep.blockIndexes)) {
    return steps.length;
  }

  return -1;
};
